<template>
  <VueDatePicker
    ref="dp"
    v-model="date"
    :format="format"
    :enable-time-picker="time"
    :inline="inline"
    :min-date="minDate"
    :max-date="maxDate"
    :prevent-min-max-navigation="
      minDate === null && maxDate === null ? false : true
    "
    hide-offset-dates
    v-bind="allowedDatesBinding"
    arrow-navigation
    :range="rangeComputed"
    :auto-apply="autoApply"
    :month-change-on-scroll="false"
    :loading="dateLoading"
    :class="{
      'availablity-calendar': sixWeeks,
      'persian-months': locale === 'fa',
    }"
    :alt-position="computedCustomPosition"
    :locale="locale == 'ar' || locale == 'fa' ? 'en' : locale"
    @internal-model-change="handleInternal"
    @update-month-year="handleMonthChange"
    @closed="selectDate"
    @open="emit('update-calendar', true)"
  >
    <!-- @closed="handleClose" -->
    <!-- @recalculate-position="handleMonthChange" -->
    <!-- @month-change="handleMonthChange" -->
    <template v-if="locale == 'fa'" #month="{ text, value }">
      {{ getMonthName(value, locale) }}
    </template>
    <template v-if="trigger" #trigger>
      <div class="text-mobile-heading-6 md:text-heading-6" :class="textsClass">
        <span
          v-if="selectedDate"
          class="text-primary-2 datepicker-trigger"
          :class="selectedDateText"
        >
          {{ format(selectedDate) }}
        </span>
        <span v-else class="text-gray-1" :class="placeholderClass">
          {{ trigger }}
        </span>
      </div>
    </template>
    <template #top-extra>
      <div class="border-b border-gray-4" v-if="isMobile">
        <div
          class="text-center text-mobile-heading-6 text-primary-1 pt-[12px] pb-[16px]"
        >
          {{ t("index.search.selectDate") }}
        </div>
        <icon-close-bold
          class="text-gray-1 text-[19px] !mb-0 cursor-pointer absolute left-6 top-4"
          @click="selectReset"
        />
      </div>
    </template>
    <template #action-buttons>
      <div v-if="range || applyAndResetButtons" class="grid grid-cols-2 gap-2">
        <BaseButton
          type="outlined"
          :label="t('reset')"
          class="w-full"
          :height="44"
          font="text-heading-5"
          @click="selectReset"
        />
        <BaseButton
          type="primary"
          :label="t('apply')"
          class="w-full"
          :height="44"
          font="text-heading-5"
          @click="applyDate"
        />
      </div>
    </template>
  </VueDatePicker>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import type { PropType } from "vue";
import { formattedDateMonthName } from "../../composables/general";
import { MONTHFA } from "../../assets/js/constants.js";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

const { t } = useI18n();
import { useCookie } from "nuxt/app";
interface IPosition extends Record<string, string | number> {
  top: number;
  left: number;
}
const { locale } = useI18n();
const props = defineProps({
  initialDate: {
    type: [Date, Array] as PropType<Date | Date[]>,
    default: null,
  },
  time: { type: Boolean, default: false },
  inline: { type: Boolean, default: false },
  autoApply: {
    type: Boolean,
    default: false,
  },
  trigger: {
    type: String,
    default: null,
  },
  openCalendar: {
    type: Boolean,
    default: false,
  },
  range: {
    type: Boolean,
    default: false,
  },
  applyAndResetButtons: {
    type: Boolean,
    default: false,
  },
  allowedDates: {
    type: Array as PropType<Date[]>,
    default: () => null,
  },
  placeholderClass: {
    type: String,
    default: "",
  },
  selectedDateText: {
    type: String,
    default: "",
  },
  textsClass: {
    type: String,
    default: "",
  },
  dateLoading: {
    type: Boolean,
    default: false,
  },
  sixWeeks: {
    type: Boolean,
    default: false,
  },
  customPosition: {
    type: Object as PropType<IPosition>,
    default: () => ({ top: 20, left: -100 }),
  },
  closeFalg: {
    type: Boolean,
    default: false,
  },
  minDate: {
    type: Date,
    default: new Date(),
  },
  maxDate: {
    type: Date,
    default: null,
  },
});

const getMonthName = (monthIndex: number, locale: string) => {
  return MONTHFA[monthIndex];
};
// const handleClose = () => {
//     if (props.closeFalg) {
//         selectDate();
//         emit("update-calendar", false);
//     }
// };
const isMobile = ref(false);
const getPosition = () => {
  return isMobile.value && props.customPosition.left == -100
    ? { top: 20, left: -60 }
    : props.customPosition;
};
const computedCustomPosition = computed(() => {
  return getPosition;
});
const handleResize = () => {
  isMobile.value = window.innerWidth <= 768;
};
const allowedDatesBinding = computed(() => {
  return props.allowedDates?.length
    ? { "allowed-dates": props.allowedDates }
    : {};
});
// const customPosition = () => ({ top: 0, left: -100 });
// const computedAllowedDates = computed(() => {
//   if (props.allowedDates === null) return [];
//   if (props.allowedDates.length > 0) return props.allowedDates;
//   else if (props.allowedDates.length == 0) return [];
//   else return [];
// });
const rangeComputed = computed(() => {
  if (props.range) {
    return { noDisabledRange: true };
  } else {
    return false;
  }
});
const emit = defineEmits([
  "update-calendar",
  "update-selected",
  "date-changed",
  "change-month",
  "apply-date",
]);
const closeCalendar = () => {
  emit("update-calendar", false);
};
const dp = ref();
const date = ref(props.initialDate);

const selectReset = () => {
  dp.value.closeMenu();
};
onMounted(() => {
  date.value = props.initialDate;
  handleResize();
});
watch(
  () => props.openCalendar,
  (val) => {
    if (val) {
      dp.value.openMenu();
      emit("update-calendar", true);
    }
  },
  { immediate: true },
);
watch(
  () => props.initialDate,
  (val) => {
    date.value = val;
  },
  { immediate: true },
);
const selectedDate = ref();
const handleInternal = (val) => {
  selectedDate.value = val;
};
const format = (dateItem: Date) => {
  // selectedDate.value = date;
  if (props.range && Array.isArray(dateItem)) {
    if (dateItem[0]) {
      let tmp = `${formattedDateMonthName(dateItem[0], locale.value)}`;
      if (dateItem[1]) {
        tmp = tmp + ` - ${formattedDateMonthName(dateItem[1], locale.value)}`;
      }
      return tmp;
    }
  }
  if (!props.range) {
    return formattedDateMonthName(dateItem, locale.value);
  }
  return "";
};
watch(date, (val) => {
  selectedDate.value = val;
  emit("date-changed", val);
  emit("update-selected", selectedDate.value);
});

const selectDate = () => {
  dp.value.selectDate();
  closeCalendar();
};
function handleMonthChange(val) {
  emit("change-month", val);
}

function applyDate() {
  if (
    props.range &&
    (selectedDate.value[1] == undefined || selectedDate.value[1] == null)
  ) {
    // toast.error("Please select both start and end dates.", {
    //   autoClose: 4000,
    // });

    // return;
    if (!selectedDate.value[1]) {
      selectedDate.value[1] = new Date(selectedDate.value[0]);
    }
  }

  // try {
  dp.value.selectDate();
  // } catch (err) {
  //   console.log(err);
  // }

  closeCalendar();

  setTimeout(() => {
    emit("apply-date");
  }, 100);
}
</script>
